import React, { useState } from "react";
import { useNavigate, Navigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { formatApiErrorDetail } from "@/lib/api";

export default function AdminLogin() {
    const { user, login } = useAuth();
    const { t, lang, toggle } = useI18n();
    const nav = useNavigate();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [submitting, setSubmitting] = useState(false);

    if (user && user !== false) {
        return <Navigate to="/admin" replace />;
    }

    const submit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            await login(email.trim(), password);
            toast.success(lang === "es" ? "Bienvenido" : "Welcome");
            nav("/admin", { replace: true });
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        } finally {
            setSubmitting(false);
        }
    };

    const inputCls =
        "w-full bg-black/40 border border-gold/25 rounded-md px-4 py-3 text-habana-cream placeholder:text-habana-cream/35 focus:outline-none focus:border-gold focus:ring-1 focus:ring-gold transition-colors";

    return (
        <div className="min-h-screen bg-habana-ink flex items-center justify-center px-4 relative overflow-hidden">
            <div className="absolute -top-40 -left-40 w-[500px] h-[500px] rounded-full bg-gold/10 blur-3xl" />
            <div className="absolute -bottom-40 -right-40 w-[500px] h-[500px] rounded-full bg-habana-red/20 blur-3xl" />

            <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                className="relative w-full max-w-md bg-habana-surface border border-gold/20 rounded-2xl p-8 md:p-10"
                data-testid="admin-login-card"
            >
                <div className="flex items-center justify-between mb-2">
                    <Link to="/" className="text-xs uppercase tracking-widest text-habana-cream/50 hover:text-gold transition-colors">
                        ← {lang === "es" ? "Volver" : "Back"}
                    </Link>
                    <button
                        onClick={toggle}
                        className="px-3 py-1 rounded-full border border-gold/40 text-xs tracking-widest uppercase text-gold hover:bg-gold hover:text-black transition-colors"
                        data-testid="admin-lang-toggle"
                    >
                        {lang === "es" ? "EN" : "ES"}
                    </button>
                </div>

                <div className="ornament mt-4">Admin</div>
                <h1 className="mt-3 font-serif text-3xl md:text-4xl text-habana-cream">
                    {t("admin_title")}
                </h1>
                <p className="text-habana-cream/60 mt-2 text-sm">
                    {lang === "es"
                        ? "Accede para gestionar menú, imágenes y contenido."
                        : "Sign in to manage menu, images and content."}
                </p>

                <form onSubmit={submit} className="mt-8 space-y-5" data-testid="admin-login-form">
                    <div>
                        <label className="text-xs tracking-widest uppercase text-gold/80">
                            {t("admin_email")}
                        </label>
                        <input
                            type="email"
                            required
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            className={`mt-2 ${inputCls}`}
                            data-testid="admin-email-input"
                            autoComplete="email"
                        />
                    </div>
                    <div>
                        <label className="text-xs tracking-widest uppercase text-gold/80">
                            {t("admin_password")}
                        </label>
                        <input
                            type="password"
                            required
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            className={`mt-2 ${inputCls}`}
                            data-testid="admin-password-input"
                            autoComplete="current-password"
                        />
                    </div>
                    <button
                        type="submit"
                        disabled={submitting}
                        className="btn-gold w-full disabled:opacity-60"
                        data-testid="admin-login-submit"
                    >
                        {submitting ? "…" : t("admin_login")}
                    </button>
                </form>
            </motion.div>
        </div>
    );
}
